import React, { useState, useEffect, useCallback } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Users,
  DollarSign,
  Star,
  RefreshCw,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';
import type { DownlineNode, DownlineTreeResponse } from '../types';
import { salesManagerService } from '../services/sales-manager.service';

// Performance colors
const getPerformanceColor = (performance: 'overperforming' | 'normal' | 'inactive') => {
  switch (performance) {
    case 'overperforming':
      return 'bg-green-100 border-green-400 text-green-800';
    case 'inactive':
      return 'bg-gray-100 border-gray-400 text-gray-600';
    default:
      return 'bg-white border-gray-200 text-gray-800';
  }
};

const getPerformanceDot = (performance: 'overperforming' | 'normal' | 'inactive') => {
  switch (performance) {
    case 'overperforming':
      return 'bg-green-500';
    case 'inactive':
      return 'bg-gray-400';
    default:
      return 'bg-blue-500';
  }
};

// Tree Node Component
const TreeNode: React.FC<{
  node: DownlineNode;
  depth: number;
  onToggle: (nodeId: string) => void;
  expandedNodes: Set<string>;
}> = ({ node, depth, onToggle, expandedNodes }) => {
  const isExpanded = expandedNodes.has(node.id);
  const hasChildren = node.children && node.children.length > 0;

  const performanceColorClass = getPerformanceColor(node.performance);
  const performanceDotClass = getPerformanceDot(node.performance);

  return (
    <div className="relative">
      {/* Connection Lines */}
      {depth > 0 && (
        <div
          className="absolute left-0 top-0 w-6 h-full"
          style={{ marginLeft: `${(depth - 1) * 32}px` }}
        >
          <div className="absolute left-3 top-0 w-px h-6 bg-gray-300"></div>
          <div className="absolute left-3 top-6 w-6 h-px bg-gray-300"></div>
        </div>
      )}

      {/* Node */}
      <div
        className={`relative ml-${depth * 8} mb-2`}
        style={{ marginLeft: `${depth * 32}px` }}
      >
        <div
          className={`flex items-start gap-3 p-4 rounded-lg border-2 transition-all duration-200 cursor-pointer hover:shadow-md ${performanceColorClass}`}
          onClick={() => hasChildren && onToggle(node.id)}
        >
          {/* Expand/Collapse Button */}
          <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center">
            {hasChildren ? (
              <button className="p-1 hover:bg-gray-200 rounded transition-colors">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
            ) : (
              <div className="w-4 h-4" />
            )}
          </div>

          {/* Avatar with Performance Indicator */}
          <div className="relative flex-shrink-0">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
              {node.firstName[0]}{node.lastName[0]}
            </div>
            <div className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${performanceDotClass}`}></div>
          </div>

          {/* Node Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-gray-900 truncate">
                {node.firstName} {node.lastName}
              </h4>
              {node.performance === 'overperforming' && (
                <span className="px-2 py-0.5 bg-green-500 text-white text-xs rounded-full flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Top Performer
                </span>
              )}
              {node.performance === 'inactive' && (
                <span className="px-2 py-0.5 bg-gray-500 text-white text-xs rounded-full flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Inactive
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 truncate">{node.email}</p>

            {/* Expanded Details */}
            {isExpanded && (
              <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-3 gap-4">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-blue-100 rounded">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Team Size</p>
                    <p className="font-semibold text-gray-900">{node.teamSize}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded">
                    <DollarSign className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total Sales</p>
                    <p className="font-semibold text-gray-900">{node.totalSales}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-yellow-100 rounded">
                    <Star className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Approval Rate</p>
                    <p className="font-semibold text-gray-900">{node.approvalRating}%</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Stats (collapsed view) */}
          {!isExpanded && (
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{node.teamSize}</span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-4 h-4" />
                <span>{node.totalSales}</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4" />
                <span>{node.approvalRating}%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <div className="relative">
          {node.children.map((child, index) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              onToggle={onToggle}
              expandedNodes={expandedNodes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Legend Component
const Legend: React.FC = () => (
  <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
    <span className="text-sm font-medium text-gray-700">Performance Legend:</span>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-green-500"></div>
      <span className="text-sm text-gray-600">Overperforming</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
      <span className="text-sm text-gray-600">Normal</span>
    </div>
    <div className="flex items-center gap-2">
      <div className="w-3 h-3 rounded-full bg-gray-400"></div>
      <span className="text-sm text-gray-600">No sales (30-60 days)</span>
    </div>
  </div>
);

// Main Downline Tree Component
export const DownlineTree: React.FC = () => {
  const [treeData, setTreeData] = useState<DownlineTreeResponse | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // Mock data for demonstration
  const mockTreeData: DownlineTreeResponse = {
    tree: {
      id: 'root',
      userId: 'user-root',
      firstName: 'Sales',
      lastName: 'Manager',
      email: 'manager@example.com',
      teamSize: 12,
      totalSales: 156,
      approvalRating: 87,
      performance: 'overperforming',
      children: [
        {
          id: 'child-1',
          userId: 'user-1',
          firstName: 'John',
          lastName: 'Smith',
          email: 'john.smith@example.com',
          teamSize: 5,
          totalSales: 45,
          approvalRating: 92,
          performance: 'overperforming',
          children: [
            {
              id: 'grandchild-1',
              userId: 'user-3',
              firstName: 'Alice',
              lastName: 'Johnson',
              email: 'alice.j@example.com',
              teamSize: 2,
              totalSales: 18,
              approvalRating: 88,
              performance: 'normal',
              children: [
                {
                  id: 'great-grandchild-1',
                  userId: 'user-5',
                  firstName: 'Emma',
                  lastName: 'Davis',
                  email: 'emma.d@example.com',
                  teamSize: 0,
                  totalSales: 8,
                  approvalRating: 90,
                  performance: 'normal',
                  children: [],
                },
              ],
            },
            {
              id: 'grandchild-2',
              userId: 'user-4',
              firstName: 'Bob',
              lastName: 'Williams',
              email: 'bob.w@example.com',
              teamSize: 0,
              totalSales: 12,
              approvalRating: 85,
              performance: 'normal',
              children: [],
            },
          ],
        },
        {
          id: 'child-2',
          userId: 'user-2',
          firstName: 'Sarah',
          lastName: 'Jones',
          email: 'sarah.j@example.com',
          teamSize: 3,
          totalSales: 0,
          approvalRating: 0,
          lastSaleDate: undefined,
          performance: 'inactive',
          children: [
            {
              id: 'grandchild-3',
              userId: 'user-6',
              firstName: 'Mike',
              lastName: 'Brown',
              email: 'mike.b@example.com',
              teamSize: 0,
              totalSales: 5,
              approvalRating: 78,
              performance: 'inactive',
              children: [],
            },
          ],
        },
        {
          id: 'child-3',
          userId: 'user-7',
          firstName: 'Emily',
          lastName: 'Taylor',
          email: 'emily.t@example.com',
          teamSize: 1,
          totalSales: 67,
          approvalRating: 95,
          performance: 'overperforming',
          children: [
            {
              id: 'grandchild-4',
              userId: 'user-8',
              firstName: 'David',
              lastName: 'Anderson',
              email: 'david.a@example.com',
              teamSize: 0,
              totalSales: 23,
              approvalRating: 89,
              performance: 'normal',
              children: [],
            },
          ],
        },
      ],
    },
    totalTeamSize: 12,
    totalSales: 156,
    averageApprovalRating: 87,
  };

  const fetchTreeData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await salesManagerService.getDownlineTree();
      setTreeData(response);

      // Auto-expand root node if it exists
      if (response.tree?.id) {
        setExpandedNodes(new Set([response.tree.id]));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load downline tree');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTreeData();
  }, [fetchTreeData]);

  const handleToggle = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const expandAll = () => {
    if (!treeData) return;

    const getAllNodeIds = (node: DownlineNode): string[] => {
      const ids = [node.id];
      if (node.children) {
        node.children.forEach((child) => {
          ids.push(...getAllNodeIds(child));
        });
      }
      return ids;
    };

    setExpandedNodes(new Set(getAllNodeIds(treeData.tree)));
  };

  const collapseAll = () => {
    if (treeData?.tree?.id) {
      setExpandedNodes(new Set([treeData.tree.id]));
    } else {
      setExpandedNodes(new Set());
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">Loading downline tree...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchTreeData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Downline Tree</h2>
        <p className="text-gray-600">Visual hierarchy showing recruits and their recruits</p>
      </div>

      {/* Summary Stats */}
      {treeData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Team Size</p>
                <p className="text-2xl font-bold text-gray-900">{treeData.totalTeamSize}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Sales</p>
                <p className="text-2xl font-bold text-gray-900">{treeData.totalSales}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Star className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Avg Approval Rating</p>
                <p className="text-2xl font-bold text-gray-900">{treeData.averageApprovalRating}%</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Legend */}
      <Legend />

      {/* Tree Controls */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Organization Tree</h3>
          <div className="flex items-center gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Collapse All
            </button>
            <button
              onClick={fetchTreeData}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Tree Visualization */}
        <div className="p-6 overflow-x-auto">
          {treeData && (
            <TreeNode
              node={treeData.tree}
              depth={0}
              onToggle={handleToggle}
              expandedNodes={expandedNodes}
            />
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">How to use the Downline Tree</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>- Click on any node to expand/collapse and see detailed information</li>
          <li>- Green nodes indicate top performers (overperforming)</li>
          <li>- Gray nodes indicate inactive members (no sales in 30-60 days)</li>
          <li>- Expanded view shows: Team Size, Total Sales, Approval Rating</li>
        </ul>
      </div>
    </div>
  );
};

export default DownlineTree;
